/**
 * Browser Tools MCP
 * 提供浏览器调试和开发工具功能
 */

// 控制台日志记录
const consoleLogs = [];
const consoleErrors = [];

// 网络请求日志
const networkLogs = [];
const networkErrors = [];

// 重写console方法来捕获日志
const originalConsoleLog = console.log;
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

console.log = (...args) => {
  consoleLogs.push({
    type: 'log',
    timestamp: new Date().toISOString(),
    message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ')
  });
  originalConsoleLog(...args);
};

console.error = (...args) => {
  consoleErrors.push({
    type: 'error',
    timestamp: new Date().toISOString(),
    message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ')
  });
  originalConsoleError(...args);
};

console.warn = (...args) => {
  consoleLogs.push({
    type: 'warn',
    timestamp: new Date().toISOString(),
    message: args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ')
  });
  originalConsoleWarn(...args);
};

// 捕获网络请求
if (window.fetch) {
  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    const url = args[0] instanceof Request ? args[0].url : String(args[0]);
    const startTime = performance.now();
    
    const logRequest = {
      url,
      method: args[1]?.method || 'GET',
      timestamp: new Date().toISOString(),
      startTime
    };
    
    return originalFetch(...args)
      .then(response => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (response.ok) {
          networkLogs.push({
            ...logRequest,
            status: response.status,
            duration: `${duration.toFixed(2)}ms`,
            success: true
          });
        } else {
          networkErrors.push({
            ...logRequest,
            status: response.status,
            duration: `${duration.toFixed(2)}ms`,
            success: false
          });
        }
        
        return response;
      })
      .catch(error => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        networkErrors.push({
          ...logRequest,
          error: error.message,
          duration: `${duration.toFixed(2)}ms`,
          success: false
        });
        
        throw error;
      });
  };
}

// 监控XHR请求
if (window.XMLHttpRequest) {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  
  XMLHttpRequest.prototype.open = function(method, url) {
    this._mcp_method = method;
    this._mcp_url = url;
    this._mcp_startTime = performance.now();
    return originalOpen.apply(this, arguments);
  };
  
  XMLHttpRequest.prototype.send = function() {
    this.addEventListener('loadend', () => {
      const endTime = performance.now();
      const duration = endTime - this._mcp_startTime;
      
      const logEntry = {
        url: this._mcp_url,
        method: this._mcp_method,
        status: this.status,
        duration: `${duration.toFixed(2)}ms`,
        timestamp: new Date().toISOString(),
      };
      
      if (this.status >= 200 && this.status < 400) {
        networkLogs.push({
          ...logEntry,
          success: true
        });
      } else {
        networkErrors.push({
          ...logEntry,
          success: false
        });
      }
    });
    
    return originalSend.apply(this, arguments);
  };
}

// 工具函数
const MCPBrowserTools = {
  getConsoleLogs: () => consoleLogs,
  getConsoleErrors: () => consoleErrors,
  getNetworkLogs: () => networkLogs,
  getNetworkErrors: () => networkErrors,
  
  takeScreenshot: () => {
    console.log('Screenshot functionality requires browser extension integration');
    alert('Screenshot captured! (模拟)');
    return { success: true, message: '截图功能需要浏览器扩展集成' };
  },
  
  wipeLogs: () => {
    consoleLogs.length = 0;
    consoleErrors.length = 0;
    networkLogs.length = 0;
    networkErrors.length = 0;
    console.log('All logs have been cleared');
    return { success: true };
  },
  
  runSEOAudit: () => {
    const results = {
      title: document.title ? { pass: true, value: document.title } : { pass: false, message: '页面缺少标题' },
      description: document.querySelector('meta[name="description"]') 
        ? { pass: true, value: document.querySelector('meta[name="description"]').getAttribute('content') } 
        : { pass: false, message: '页面缺少描述' },
      h1: document.querySelector('h1') 
        ? { pass: true, count: document.querySelectorAll('h1').length } 
        : { pass: false, message: '页面缺少H1标签' },
      images: Array.from(document.querySelectorAll('img')).filter(img => !img.alt).length === 0
        ? { pass: true }
        : { pass: false, message: `${Array.from(document.querySelectorAll('img')).filter(img => !img.alt).length}个图片缺少alt属性` },
      links: Array.from(document.querySelectorAll('a')).filter(a => !a.textContent.trim()).length === 0
        ? { pass: true }
        : { pass: false, message: `${Array.from(document.querySelectorAll('a')).filter(a => !a.textContent.trim()).length}个链接缺少文本内容` }
    };
    
    console.table(results);
    return results;
  },
  
  runNextJSAudit: () => {
    console.log('Running NextJS audit...');
    const results = {
      nextJSDetected: window.__NEXT_DATA__ ? true : false,
      dynamicImports: false,
      performanceMetrics: {
        LCP: 'Not available without browser API',
        FID: 'Not available without browser API',
        CLS: 'Not available without browser API'
      }
    };
    
    console.table(results);
    return results;
  },
  
  runDebuggerMode: () => {
    console.log('Debug mode activated');
    
    // 检查DOM加载问题
    const brokenImages = Array.from(document.querySelectorAll('img')).filter(img => !img.complete || img.naturalHeight === 0);
    
    // 检查JS错误
    const jsErrors = consoleErrors.filter(error => error.type === 'error');
    
    // 网络错误
    const apiErrors = networkErrors.length;
    
    const results = {
      brokenImages: brokenImages.length,
      jsErrors: jsErrors.length,
      apiErrors: apiErrors,
      totalIssues: brokenImages.length + jsErrors.length + apiErrors
    };
    
    console.table(results);
    return results;
  },
  
  runAuditMode: () => {
    console.log('Running full audit mode...');
    
    // 合并所有审计结果
    const seoResults = this.runSEOAudit();
    const debugResults = this.runDebuggerMode();
    
    // 辅助功能检查
    const a11yIssues = {
      missingAlt: Array.from(document.querySelectorAll('img')).filter(img => !img.alt).length,
      missingLabels: Array.from(document.querySelectorAll('input')).filter(input => !input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby') && !input.id).length,
      contrastIssues: '需要浏览器扩展支持'
    };
    
    const results = {
      seo: seoResults,
      debug: debugResults,
      accessibility: a11yIssues
    };
    
    console.log('完整审计结果：', results);
    return results;
  }
};

// 导出工具
window.MCPBrowserTools = MCPBrowserTools;
export default MCPBrowserTools; 